import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PackageCard from "@/components/PackageCard";
import { ArrowLeft, ArrowRight, MapPin, Check } from "lucide-react";

export default function DestinationDetail() {
  const { slug } = useParams();
  const [dest, setDest] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/destinations/${slug}`),
      api.get(`/packages?destination=${slug}`),
    ]).then(([d, p]) => {
      setDest(d.data);
      setPackages(p.data);
    }).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-5 py-16 text-slate-500">Loading…</div>;
  if (!dest) return <div className="max-w-7xl mx-auto px-5 py-16">Destination not found. <Link to="/destinations" className="text-blue-600">Back</Link></div>;

  return (
    <div data-testid={`destination-detail-${slug}`}>
      {/* Hero */}
      <section className="relative">
        <div className="h-[380px] md:h-[460px] relative overflow-hidden">
          <img src={dest.hero_image} alt={dest.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 -mt-40 relative">
          <Link to="/destinations" className="inline-flex items-center gap-2 text-white/90 text-sm mb-3 hover:text-white" data-testid="destination-back-link">
            <ArrowLeft className="w-4 h-4" /> All destinations
          </Link>
          <p className="text-orange-300 font-bold uppercase tracking-widest text-xs mb-2">{dest.tagline}</p>
          <h1 className="font-display text-5xl md:text-6xl font-extrabold text-white leading-none">{dest.name}</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-3">About the destination</h2>
          <p className="text-slate-600 leading-relaxed mb-6">{dest.long_description}</p>

          <h3 className="font-display text-lg font-bold text-slate-900 mb-3">What you'll experience</h3>
          <ul className="grid sm:grid-cols-2 gap-2 mb-8">
            {dest.highlights?.map((h) => (
              <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {h}
              </li>
            ))}
          </ul>

          {dest.cities?.length ? (
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900 mb-3">Cities you may visit</h3>
              <div className="flex flex-wrap gap-2 mb-8">
                {dest.cities.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 text-sm font-medium text-slate-700">
                    <MapPin className="w-3.5 h-3.5" /> {c}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-3xl bg-white border border-slate-100 shadow-lg shadow-blue-500/10 p-6 sticky top-24">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Available from</p>
            <div className="flex flex-wrap gap-1.5 mb-6">
              {dest.available_from?.map((c) => (
                <span key={c} className="px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">{c}</span>
              ))}
            </div>
            <Link
              to={`/apply?destination=${dest.slug}`}
              data-testid="destination-apply-cta"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm shadow-lg shadow-orange-500/30 transition-colors mb-3"
            >
              Apply for {dest.name} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={`/programs?destination=${dest.slug}`}
              data-testid="destination-view-programs"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold text-sm transition-colors"
            >
              View Programs
            </Link>
          </div>
        </aside>
      </section>

      {packages.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
          <h2 className="font-display text-3xl font-extrabold text-slate-900 mb-8">Packages for {dest.name}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
